import { useEffect, useState } from 'react'
import { useMutation, useQuery, useQueryClient, type QueryClient } from '@tanstack/react-query'
import { purchaseOrdersApi, type PurchaseOrderListParams } from '@/api/purchaseOrdersApi'
import { useTenant } from '@/providers/TenantProvider'
import type {
  CreatePurchaseOrderPayload,
  PoTemplateLayout,
  PurchaseOrderLineInput,
  UpdatePurchaseOrderPayload,
} from '@/types/purchaseOrders'

const KEY = 'purchase-orders'

/**
 * Every PO write moves a material's On order (open = DRAFT + SENT), and with
 * it the re-order verdicts, so the material and Re-Order queries — all keyed
 * under `['inventory', 'materials']` — are refreshed along with the POs. Sending,
 * receiving and cancelling an order open and close its task (z8nrz7fjzc), so
 * the task lists are refreshed too.
 */
function invalidateAfterWrite(qc: QueryClient) {
  void qc.invalidateQueries({ queryKey: [KEY] })
  void qc.invalidateQueries({ queryKey: ['inventory', 'materials'] })
  void qc.invalidateQueries({ queryKey: ['inventory', 'material'] })
  void qc.invalidateQueries({ queryKey: ['maintenance-events'] })
}

// ─── Queries ────────────────────────────────────────────────────────────────

export function usePurchaseOrders(params: PurchaseOrderListParams, options: { enabled?: boolean } = {}) {
  const { selectedTenantId: tenantId } = useTenant()
  return useQuery({
    queryKey: [KEY, 'list', tenantId, params],
    enabled: !!tenantId && (options.enabled ?? true),
    queryFn: () => purchaseOrdersApi.list(tenantId as string, params),
  })
}

/** One order in full. Pass `null` to skip. */
export function usePurchaseOrder(id: string | null) {
  const { selectedTenantId: tenantId } = useTenant()
  return useQuery({
    queryKey: [KEY, 'detail', tenantId, id],
    enabled: !!tenantId && !!id,
    queryFn: () => purchaseOrdersApi.get(tenantId as string, id as string),
  })
}

/** An earlier revision's snapshot. A revision never changes, so it is never stale. */
export function usePurchaseOrderRevision(id: string | null, revision: number | null) {
  const { selectedTenantId: tenantId } = useTenant()
  return useQuery({
    queryKey: [KEY, 'revision', tenantId, id, revision],
    enabled: !!tenantId && !!id && revision != null,
    queryFn: () => purchaseOrdersApi.getRevision(tenantId as string, id as string, revision as number),
    staleTime: Infinity,
  })
}

/** Supplier and first line proposed for ordering a material. Pass `null` to skip. */
export function usePoPrefill(materialId: string | null) {
  const { selectedTenantId: tenantId } = useTenant()
  return useQuery({
    queryKey: [KEY, 'prefill', tenantId, materialId],
    enabled: !!tenantId && !!materialId,
    queryFn: () => purchaseOrdersApi.prefill(tenantId as string, materialId as string),
  })
}

/**
 * Fetch a prefill on demand — for a material picked into an order that is
 * already open, where there is no render to hang `usePoPrefill` on. Shares the
 * hook's cache entry.
 */
export function useFetchPoPrefill() {
  const { selectedTenantId: tenantId } = useTenant()
  const qc = useQueryClient()
  return (materialId: string) => {
    if (!tenantId) return Promise.reject(new Error('No tenant selected'))
    return qc.fetchQuery({
      queryKey: [KEY, 'prefill', tenantId, materialId],
      queryFn: () => purchaseOrdersApi.prefill(tenantId, materialId),
    })
  }
}

// ─── Mutations ──────────────────────────────────────────────────────────────

function useTenantMutation<V, R>(fn: (tenantId: string, v: V) => Promise<R>) {
  const { selectedTenantId: tenantId } = useTenant()
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (v: V) => {
      if (!tenantId) throw new Error('No tenant selected')
      return fn(tenantId, v)
    },
    onSuccess: () => invalidateAfterWrite(qc),
  })
}

export function useCreatePurchaseOrder() {
  return useTenantMutation((t, payload: CreatePurchaseOrderPayload) => purchaseOrdersApi.create(t, payload))
}

export function useUpdatePurchaseOrder() {
  return useTenantMutation((t, v: { id: string; payload: UpdatePurchaseOrderPayload }) =>
    purchaseOrdersApi.update(t, v.id, v.payload),
  )
}

export function useAddPurchaseOrderLine() {
  return useTenantMutation((t, v: { id: string; line: PurchaseOrderLineInput }) =>
    purchaseOrdersApi.addLine(t, v.id, v.line),
  )
}

export function useSendPurchaseOrder() {
  return useTenantMutation((t, id: string) => purchaseOrdersApi.send(t, id))
}

export function useReceivePurchaseOrder() {
  return useTenantMutation((t, id: string) => purchaseOrdersApi.receive(t, id))
}

// ─── Template ───────────────────────────────────────────────────────────────

const TEMPLATE_KEY = 'po-template'

/** The tenant's current PO template (the built-in default, as version 0, until one is saved). */
export function usePoTemplate() {
  const { selectedTenantId: tenantId } = useTenant()
  return useQuery({
    queryKey: [TEMPLATE_KEY, 'current', tenantId],
    enabled: !!tenantId,
    queryFn: () => purchaseOrdersApi.getTemplate(tenantId as string),
  })
}

export function usePoTemplateVersions(enabled = true) {
  const { selectedTenantId: tenantId } = useTenant()
  return useQuery({
    queryKey: [TEMPLATE_KEY, 'versions', tenantId],
    enabled: !!tenantId && enabled,
    queryFn: () => purchaseOrdersApi.listTemplateVersions(tenantId as string),
  })
}

/** One saved version. A version never changes once saved. */
export function usePoTemplateVersion(version: number | null) {
  const { selectedTenantId: tenantId } = useTenant()
  return useQuery({
    queryKey: [TEMPLATE_KEY, 'version', tenantId, version],
    enabled: !!tenantId && version != null,
    queryFn: () => purchaseOrdersApi.getTemplateVersion(tenantId as string, version as number),
    staleTime: Infinity,
  })
}

function useTemplateMutation<V, R>(fn: (tenantId: string, v: V) => Promise<R>) {
  const { selectedTenantId: tenantId } = useTenant()
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (v: V) => {
      if (!tenantId) throw new Error('No tenant selected')
      return fn(tenantId, v)
    },
    onSuccess: (r) => {
      // Save and reset answer with the new current template: show it at once,
      // so the editor never compares against the version it just replaced.
      if (r && typeof r === 'object' && 'layout' in r) qc.setQueryData([TEMPLATE_KEY, 'current', tenantId], r)
      void qc.invalidateQueries({ queryKey: [TEMPLATE_KEY] })
    },
  })
}

export function useSavePoTemplate() {
  return useTemplateMutation((t, v: { layout: PoTemplateLayout; note?: string }) =>
    purchaseOrdersApi.saveTemplate(t, v.layout, v.note),
  )
}

export function useResetPoTemplate() {
  return useTemplateMutation((t, _v: void) => purchaseOrdersApi.resetTemplate(t))
}

/** Upload only — the key lands in the layout, which is saved with the template. */
export function useUploadPoLogo() {
  const { selectedTenantId: tenantId } = useTenant()
  return useMutation({
    mutationFn: (file: File) => {
      if (!tenantId) throw new Error('No tenant selected')
      return purchaseOrdersApi.uploadLogo(tenantId, file)
    },
  })
}

/**
 * The logo as an object URL an <img> can show, or null. Fetched with the
 * session (the route is authenticated), and the URL is revoked when the key
 * changes or the caller unmounts, so previews do not leak blobs.
 */
export function usePoLogo(key: string | null | undefined): string | null {
  const { selectedTenantId: tenantId } = useTenant()
  const { data: blob } = useQuery({
    queryKey: [TEMPLATE_KEY, 'logo', tenantId, key],
    enabled: !!tenantId && !!key,
    queryFn: () => purchaseOrdersApi.getLogo(tenantId as string, key as string),
    staleTime: Infinity,
  })
  const [url, setUrl] = useState<string | null>(null)
  useEffect(() => {
    if (!blob || !key) {
      setUrl(null)
      return
    }
    const next = URL.createObjectURL(blob)
    setUrl(next)
    return () => URL.revokeObjectURL(next)
  }, [blob, key])
  return url
}

export function useCancelPurchaseOrder() {
  return useTenantMutation((t, v: { id: string; reason: string }) => purchaseOrdersApi.cancel(t, v.id, v.reason))
}
